import React from 'react';
import PropTypes from 'prop-types';
import { Tag } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import { getEventIcon } from '../eventIcons';
import CopyButton from '../CopyButton';
import style from './index.scss';

// Keyed on the TAG, not on event.status. A flagged transaction has
// status 'neutral' (the BE hardcodes severity "Neutral" for every transaction),
// so keying on status would paint the design's red `flagged` pill orange.
const STATUS_TAG_COLOR = {
  flagged: 'red',
  failed: 'red',
  neutral: 'orange',
  success: 'green'
};

// The icon box was driven by $ringColor / $iconColor props under
// styled-components. SCSS modules cannot take runtime props, so each state is a
// modifier class instead.
//
// `flagged` shares the failed box: both are the alarming state, and the design
// pairs the red pill with a red icon.
const ICON_STATUS_CLASS = {
  flagged: style.panelIconFailed,
  success: style.panelIconSuccess,
  neutral: style.panelIconNeutral,
  failed: style.panelIconFailed
};

// FIN-14030 — a dash (toEvents' "no id" placeholder) or a blank value is not an
// identifier, so it gets no copy control — same rule as the header's Txn ID.
const isCopyable = (value) => {
  const text = value == null ? '' : String(value).trim();
  return text !== '' && text !== '—' && text !== '-';
};

const renderValue = (row) => {
  const value = (
    <span
      className={`${style.panelValue}${
        row.link ? ` ${style.panelValueLink}` : ''
      }`}
      title={row.title || String(row.value)}>
      {row.value}
    </span>
  );
  // FIN-14030 — the value truncates at 180px and a native title cannot be
  // selected, so identifier (`link`) rows — the Event ID — carry the shell's copy
  // button, the header's Txn ID control. It copies the TITLE when there is one: a
  // grouped row's value is only the first id, its title every member's. Wrapped
  // so the row keeps two flex children and space-between still pins the value
  // right.
  if (!row.link || !isCopyable(row.value)) return value;
  return (
    <span className={style.panelValueWithCopy}>
      {value}
      <CopyButton
        value={row.title || String(row.value)}
        ariaLabel="Copy event ID"
      />
    </span>
  );
};

const EventDetailPanel = ({ event, onClose }) => {
  if (!event) return null;
  const Icon = getEventIcon(event.icon);
  // KEYED ON THE TAG FIRST, falling back to status when there is no pill. The two
  // must agree: a transaction's status is always 'neutral' (hardcoded on the BE),
  // so keying on status alone put an AMBER icon box next to a RED `flagged` pill
  // on the same row.
  const iconClass =
    ICON_STATUS_CLASS[event.statusTag] ||
    ICON_STATUS_CLASS[event.status] ||
    ICON_STATUS_CLASS.neutral;

  return (
    <div className={style.detailPanel}>
      <div className={style.panelHead}>
        <span className={`${style.panelIcon} ${iconClass}`}>
          <Icon />
        </span>
        <span className={style.panelTitle}>{event.label}</span>
        {event.statusTag ? (
          <Tag
            className={style.statusTag}
            color={STATUS_TAG_COLOR[event.statusTag] || 'default'}>
            {event.statusTag}
          </Tag>
        ) : null}
        <button
          type="button"
          className={style.panelClose}
          aria-label="Close event details"
          onClick={onClose}>
          <CloseCircleOutlined />
        </button>
      </div>

      <div className={style.panelBody}>
        {(event.details || []).map((row, i) => {
          if (row.divider) {
            // eslint-disable-next-line react/no-array-index-key
            return <div className={style.panelDivider} key={`divider-${i}`} />;
          }
          // The design heads the typed block with its own caption
          // ("ACTIVITY DETAILS · LOGIN"). Built in panelSections.js from the
          // event kind plus detail.eventType.
          if (row.section) {
            return (
              <div className={style.panelSection} key={row.section}>
                {row.section}
              </div>
            );
          }
          return (
            <div className={style.panelRow} key={row.label}>
              <span className={style.panelLabel}>{row.label}</span>
              {/* Identifier rows render in the primary colour — see the `link`
                  flag in panelSections.js. Not an <a>: there is nowhere to
                  navigate to yet, and a link that does nothing is worse than a
                  coloured value. */}
              {/* `row.title` when the value is a SUMMARY. A grouped
                  identifier reads "first +N more", and this cell is
                  `max-width: 180px` with `truncate` (the design's
                  `max-w-[180px]`), so without the hover the rest of the ids
                  would be unreachable. Falls back to the value itself, which is
                  also worth having: a long single id truncates too. */}
              {renderValue(row)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

EventDetailPanel.propTypes = {
  event: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

export default EventDetailPanel;
